'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScheduleEventMultiPagePrint } from '@pickleballinc/ui/components/team-leagues/prints/Schedule/ScheduleEventSheetPrint';
import { Checkbox } from '@pickleballinc/ui/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { PrintButton } from '@/components/atoms/PrintButton';
import { SchedulerView } from '@/components/constants/enums';
import { SchedulePeriodDropdown } from '@/components/molecules/SchedulePeriodDropdown';
import { SchedulerPrintSidebar } from '@/components/molecules/scheduler/SchedulerPrintSidebar';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { usePrintScheduleQuery } from '@/hooks/schedule/usePrintScheduleQuery';
import { ScheduleMatchupPeriodsDto } from '@/infrastructure/network/dto/ScheduleMatchupPeriodsDto';
import { ViewOption } from '@/lib/types';
import { formatTeamLeagueLabels } from '@/utils/helpers';
import {
	SessionFilterType,
	filterScheduleBySession,
	shouldUseSessions,
	getMinMaxDatesFromSelection,
	buildPrintFilename
} from '@/utils/scheduleFilters';

interface SchedulerHeaderButtonsProps {
	handleSelectView: (value: string | number) => void;
	viewOptions: ViewOption[];
	isPendingScheduleView?: boolean;
	handleShowOnlyMyScheduler?: (value: boolean) => void;
	periods: ScheduleMatchupPeriodsDto[];
	setStartDate: (date: string) => void;
	setEndDate: (date: string) => void;
	selectedView: string;
	selectedStartDate?: string;
	selectedEndDate?: string;
	scheduleGroupUuid?: string;
}

export const SchedulerHeaderButtons = ({
	handleSelectView,
	viewOptions,
	isPendingScheduleView,
	handleShowOnlyMyScheduler,
	periods,
	setStartDate,
	setEndDate,
	selectedView,
	selectedStartDate,
	selectedEndDate,
	scheduleGroupUuid
}: SchedulerHeaderButtonsProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	// useScheduleGroups means team league uses event groups
	const useScheduleGroups = teamLeagueInfo.useScheduleGroups;
	const labels = formatTeamLeagueLabels(teamLeagueInfo);
	const [printSidebarOpen, setPrintSidebarOpen] = useState(false);
	const [sessionFilter, setSessionFilter] = useState<SessionFilterType>('all');
	const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
	const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

	// Determine query parameters based on session filter
	const useSessions = shouldUseSessions(sessionFilter);

	const { data: scheduleData, isFetching: isFetchingScheduleData } = usePrintScheduleQuery(scheduleGroupUuid, useSessions, printSidebarOpen);

	// Compute filtered data based on current filter and data
	const getPrintData = () => filterScheduleBySession(scheduleData?.data ?? null, sessionFilter, dateFrom, dateTo);
	const printData = getPrintData();
	const contentRef = useRef<HTMLDivElement>(null);

	// Min and max dates for DateTimePicker validation
	const { minDate, maxDate } = getMinMaxDatesFromSelection(selectedStartDate, selectedEndDate);
	// Persist last non-empty title to avoid flicker during refetch
	const lastTitleRef = useRef<string | null>(null);
	if (scheduleData?.data?.eventName) {
		lastTitleRef.current = scheduleData.data.eventName;
	}
	const currentTitle = lastTitleRef.current || 'Schedule';
	const generatePrintFilename = () => buildPrintFilename(currentTitle, selectedStartDate, selectedEndDate);

	const noMatchesForPeriod = !isFetchingScheduleData && (printData?.dates?.length ?? 0) === 0;

	const canShowPrintButton = useScheduleGroups;

	const reactToPrintFn = useReactToPrint({
		contentRef,
		documentTitle: generatePrintFilename()
	});

	const handlePrintWithSession = (data: { sessionSelection: SessionFilterType; dateFrom?: Date; dateTo?: Date }) => {
		setSessionFilter(data.sessionSelection);
		setDateFrom(data.dateFrom);
		setDateTo(data.dateTo);

		if (printData) {
			setTimeout(() => {
				reactToPrintFn();
			}, 100);
		}
	};

	useEffect(() => {
		if (!periods) return;

		if (periods.length > 0) {
			const firstPeriod = periods[0];

			setStartDate(firstPeriod?.startDate || '');
			setEndDate(firstPeriod?.endDate || '');
		}
	}, [selectedView, periods, setStartDate, setEndDate]);

	const scheduleTitle = isPendingScheduleView ? 'Pending schedule' : 'Final Schedule';

	const handleViewChange = (value: string) => {
		handleSelectView(value);
	};

	const viewTypeOptions = viewOptions.map((option) => ({ value: option.value, label: option.label }));

	return (
		<div className="print-hidden">
			<div>
				<p className="pb-6 text-2xl font-semibold">{scheduleTitle}</p>
			</div>
			<div className="flex flex-col gap-6 items-start sm:items-center sm:flex-row sm:justify-between">
				<div className="flex items-start  flex-col justify-between gap-4">
					{periods?.length > 0 && (
						<p>
							{useScheduleGroups
								? 'Select event'
								: selectedView === SchedulerView.Week
									? 'Select week'
									: selectedView === SchedulerView.Schedule
										? 'Select month'
										: null}
						</p>
					)}

					<SchedulePeriodDropdown
						periods={periods}
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						isMonthView={selectedView === SchedulerView.Schedule}
					/>
				</div>
				<div className="flex justify-between gap-2 rounded-xl sm:justify-normal">
					<Select onValueChange={(value) => handleViewChange(value)} value={selectedView}>
						<SelectTrigger
							className="bg-white focus:ring-0"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select a view" />
						</SelectTrigger>
						<SelectContent>
							{viewTypeOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{/* Printing is available only for event groups that have matchups. (We need BE support for other cases) */}
					{Boolean(canShowPrintButton) && <PrintButton onClick={() => setPrintSidebarOpen(true)} className="border-gray-300 shadow-sm" />}
				</div>
			</div>
			{!useScheduleGroups && periods?.length > 0 && (
				<p className="pt-1">
					{selectedView === SchedulerView.Week
						? `Only weeks with ${labels.matchupPluralTitle} scheduled will appear`
						: selectedView === SchedulerView.Schedule
							? `Only months with ${labels.matchupPluralTitle} scheduled will appear`
							: null}
				</p>
			)}

			{isPendingScheduleView ? (
				<p className="pt-6 text-sm">
					This schedule is pending - please approve the matches so the calendar can be finalized. If a date/time/location does not work,
					please propose new.
				</p>
			) : null}

			{isPendingScheduleView && handleShowOnlyMyScheduler ? (
				<div className="flex pt-6">
					<Checkbox
						className="size-4 cursor-pointer rounded-md data-[state=checked]:bg-primary-100 data-[state=checked]:text-primary"
						onCheckedChange={(checked: boolean) => {
							handleShowOnlyMyScheduler?.(checked);
						}}
					/>
					<p className="pl-2 text-sm font-bold text-gray-700">Show only my schedule</p>
				</div>
			) : null}

			<div ref={contentRef} className="print:block hidden">
				{printData ? <ScheduleEventMultiPagePrint schedule={printData} sessionType={sessionFilter} /> : null}
			</div>

			<SchedulerPrintSidebar
				key={`${scheduleGroupUuid}-${selectedStartDate}-${selectedEndDate}`}
				open={printSidebarOpen}
				onOpenChange={setPrintSidebarOpen}
				onPrint={handlePrintWithSession}
				eventTitle={currentTitle}
				minDate={minDate}
				maxDate={maxDate}
				onSessionChange={setSessionFilter}
				isFetchingScheduleData={isFetchingScheduleData}
				noMatches={noMatchesForPeriod}
				onDateRangeChange={({ dateFrom: from, dateTo: to }) => {
					setDateFrom(from);
					setDateTo(to);
				}}
			/>
		</div>
	);
};
